/**
 * Button Component
 * Reusable button component following design system principles
 * Integrated with the theme system for consistent styling
 */

import React from 'react';
import { type ButtonProps } from '../../types';
import { useSafeThemeClasses, useSafeTheme } from '@/shared/theme';

interface ExtendedButtonProps extends ButtonProps, Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  children: React.ReactNode;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

export const Button: React.FC<ExtendedButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  icon,
  iconPosition = 'left',
  onClick,
  className = '',
  ...props
}) => {
  const theme = useSafeThemeClasses();
  const { variant: themeVariant } = useSafeTheme();
  const isFantasy = themeVariant === 'fantasy';

  // Base classes with proper alignment and centering
  const baseClasses = 'transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 inline-flex items-center justify-center relative overflow-hidden group';

  const variantClasses = {
    primary: `${theme.buttonPrimary} ${theme.buttonTextPrimary}`,
    secondary: `${theme.buttonSecondary} ${theme.buttonTextSecondary}`,
    outline: `${theme.buttonSecondary} ${theme.buttonTextSecondary}`,
    ghost: `${theme.buttonGhost} ${theme.buttonTextGhost}`,
  };

  // Fantasy uses a slightly less-rounded, more "carved" silhouette than
  // Glass's soft rounded-xl/rounded-full look.
  const sizeClasses = isFantasy
    ? {
        sm: 'px-3 py-1.5 text-sm rounded',
        md: 'px-4 py-2 text-base rounded-md',
        lg: 'px-6 py-3 text-lg rounded-lg',
      }
    : {
        sm: 'px-3 py-1.5 text-sm rounded-md',
        md: 'px-4 py-2 text-base rounded-lg',
        lg: 'px-6 py-3 text-lg rounded-xl',
      };

  const classes = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    fullWidth ? 'w-full' : '',
    disabled || loading ? 'opacity-50 cursor-not-allowed' : '',
    className,
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {/* Elegant shimmer overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-r from-transparent ${isFantasy ? 'via-[#f4e4b8]/30' : 'via-white/10'} to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-out pointer-events-none`}
      ></div>
      
      <span className="relative z-10 flex items-center justify-center gap-2">
        {loading && (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
        )}
        {icon && iconPosition === 'left' && !loading && (
          <span className="transition-transform duration-200 group-hover:scale-110 flex items-center">{icon}</span>
        )}
        <span className="whitespace-nowrap">{children}</span>
        {icon && iconPosition === 'right' && !loading && (
          <span className="transition-transform duration-200 group-hover:scale-110 flex items-center">{icon}</span>
        )}
      </span>
    </>
  );

  return (
    <button
      className={classes}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {content}
    </button>
  );
};

export default Button;
